import { Injectable } from '@angular/core';

/**
 * Carries a "hire this one" intent across the centralized-auth bounce.
 *
 * WHY THIS EXISTS
 * crewdle.com's catalogue pages link to `/marketplace?hire=<slug>`. For a
 * signed-out visitor the auth guard calls `bridge.login()`, and
 * @crewdle/auth-bridge builds its redirect_uri from
 * `window.location.origin + window.location.pathname` — no `search`. So the
 * user comes back to `/marketplace` (pathname survives) with the query string
 * gone, landing in the full grid with no idea which agent they clicked
 * (Vincent, 2026-09-04).
 *
 * Fixing auth-bridge instead would mean republishing a package 10 apps consume
 * and widening the auth portal's accepted redirect URIs, so the intent is
 * stashed here instead: same outcome, blast radius of one app.
 *
 * sessionStorage, not localStorage: the intent is meaningful for exactly one
 * navigation. It survives the cross-origin bounce (storage is per origin, and
 * the tab returns to this origin) but must not resurface in a new tab a week
 * later and hijack someone's visit to the marketplace.
 */
@Injectable({ providedIn: 'root' })
export class HireIntentService {
  private static readonly KEY = 'crewdle_hire_intent';

  /**
   * Remember a slug if there is one. Called by the auth guard before it hands
   * off to the portal, so it must never throw — storage can be unavailable in
   * private modes and a failed stash is a lost deep link, not a broken login.
   */
  capture(slug: string | null | undefined): void {
    const value = (slug ?? '').trim();
    if (!value) return;
    try {
      sessionStorage.setItem(HireIntentService.KEY, value);
    } catch {
      // Storage disabled — the deep link degrades to the plain marketplace.
    }
  }

  /** Read and clear, so a back-navigation does not re-apply the filter. */
  consume(): string | null {
    try {
      const value = sessionStorage.getItem(HireIntentService.KEY);
      if (value) sessionStorage.removeItem(HireIntentService.KEY);
      return value || null;
    } catch {
      return null;
    }
  }
}
